// relativeTime — shared "time ago" formatter.
//
// 19BM-D Phase 6b: extracted from three duplicated implementations
// (message-center.tsx, notification-panel.tsx, history-view.tsx). Each had
// its own copy with slightly different thresholds + signatures. This util
// unifies on the most complete version: seconds-level granularity + a
// locale-aware fallback for anything older than 7 days.
//
// Contract:
//   - Input: epoch ms (number) OR ISO string (string). Both accepted so
//     callers don't need to convert.
//   - Output: localized "Xs/m/h/d ago" or a locale-formatted date for >7d.
//   - Locale: passed in (callers already have useLocale()). zh → 中文,
//     everything else → English.

import { useT, useLocale, type TFunction } from '../i18n/index.js';

/** Format a timestamp as "time ago" or a locale date for old timestamps.
 *
 * Takes either epoch-ms or ISO string so both call sites work without
 * pre-conversion. Thresholds: <10s "just now", then s/m/h/d, then >7d
 * falls back to a locale-formatted absolute date. */
export function formatRelativeTime(input: number | string, locale: string, t: TFunction): string {
  const ts = typeof input === 'string' ? new Date(input).getTime() : input;
  if (!Number.isFinite(ts)) return '—';
  const diff = Date.now() - ts;
  const seconds = Math.floor(diff / 1000);
  if (seconds < 10) return t('relativeTime.justNow');
  if (seconds < 60) return t('relativeTime.secondsAgo', { count: seconds });
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return t('relativeTime.minutesAgo', { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('relativeTime.hoursAgo', { count: hours });
  const days = Math.floor(hours / 24);
  if (days < 7) return t('relativeTime.daysAgo', { count: days });
  // >7d: locale-formatted absolute date (preserves history-view's original
  // behavior of showing a real date instead of "8d ago" / "90d ago").
  const dateStr = new Date(ts).toLocaleDateString(locale === 'zh' ? 'zh-CN' : 'en-GB');
  return t('relativeTime.older', { date: dateStr });
}

/** Hook variant for components that want locale + t wired automatically.
 *  Returns a stable (input) => string formatter. */
export function useRelativeTime(): (input: number | string) => string {
  const t = useT();
  const { locale } = useLocale();
  return (input: number | string) => formatRelativeTime(input, locale, t);
}
